/**
 * 네비게이션 유틸리티 함수들
 * 환경에 따른 경로 접두사 관리, i18n 라우팅, VSCode 환경 감지 등
 */ function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import React from 'react';
import { Link, useNavigate } from 'react-router';
/**
 * VSCode 환경인지 확인하는 함수
 */ export var isVSCodeEnvironment = function() {
    // VSCode Webview 환경을 감지하는 여러 방법 적용
    if (typeof window !== 'undefined') {
        // 1. window.acquireVsCodeApi 존재 여부 확인 - 가장 신뢰할 수 있는 방법
        var hasVsCodeApi = typeof window.acquireVsCodeApi === 'function';
        // 2. URL에 vscode 관련 패턴이 있는지 확인
        var urlContainsVSCode = window.location && (window.location.href.includes('vscode-webview') || window.location.href.includes('vscode-file')) || false;
        // 3. 정의된 전역 VSCode 변수 확인
        var hasVSCodeGlobal = 'vscodeApi' in window;
        // iframe 확인은 제거 - 일반 웹에서도 iframe을 사용할 수 있음
        // VSCode 환경은 위의 세 가지 조건 중 하나를 만족해야 함
        return hasVsCodeApi || urlContainsVSCode || hasVSCodeGlobal;
    }
    return false;
};
/**
 * 현재 환경에 맞는 경로 접두사를 반환하는 함수
 */ export var getPathPrefix = function() {
    // VS Code webview에서는 prefix가 필요 없음
    if (isVSCodeEnvironment()) {
        return '';
    }
    // 웹 환경에서는 현재 언어를 prefix로 사용
    var currentLanguage = getCurrentLanguage();
    return currentLanguage; // '/' 없이 반환 (usePrefixedNavigate에서 추가함)
};
/**
 * 현재 설정된 언어를 반환하는 함수
 */ export var getCurrentLanguage = function() {
    // 1. URL에서 언어 코드 추출 (예: /ko/home -> ko)
    if (typeof window !== 'undefined' && window.location) {
        var pathSegments = window.location.pathname.split('/').filter(Boolean);
        var supportedLanguages = [
            'en',
            'ko',
            'ja'
        ];
        if (pathSegments.length > 0 && supportedLanguages.includes(pathSegments[0])) {
            return pathSegments[0];
        }
    }
    // 2. localStorage에서 언어 확인
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        var storedLanguage = localStorage.getItem('i18nextLng');
        if (storedLanguage && [
            'en',
            'ko',
            'ja'
        ].includes(storedLanguage)) {
            return storedLanguage;
        }
    }
    // 3. 브라우저 언어 감지
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined' && navigator.language) {
        var browserLang = navigator.language.split('-')[0];
        if ([
            'ko',
            'ja'
        ].includes(browserLang)) {
            return browserLang;
        }
    }
    // 4. 기본값: 영어
    return 'en';
};
/**
 * 환경에 맞게 라우팅 경로를 생성하는 함수
 * @param path 기본 경로 (접두사 제외)
 * @returns 환경에 맞게 접두사가 추가된 전체 경로
 */ export var getRoutePath = function(path) {
    // VS Code webview에서는 prefix 없이 그대로 반환
    if (isVSCodeEnvironment()) {
        var pathWithSlash = path.startsWith('/') ? path : "/".concat(path);
        return pathWithSlash;
    }
    // 웹 환경에서는 언어 접두사 추가
    var prefix = getPathPrefix();
    var pathWithSlash1 = path.startsWith('/') ? path : "/".concat(path);
    // 이미 언어 접두사가 있으면 그대로 반환
    var supportedLanguages = [
        'en',
        'ko',
        'ja'
    ];
    var pathSegments = pathWithSlash1.split('/').filter(Boolean);
    if (pathSegments.length > 0 && supportedLanguages.includes(pathSegments[0])) {
        return pathWithSlash1;
    }
    // 언어 접두사 추가
    var prefixedSegment = prefix ? "/".concat(prefix) : '';
    return "".concat(prefixedSegment).concat(pathWithSlash1);
};
/**
 * 환경에 맞는 경로 접두사를 자동으로 적용하는 Link 컴포넌트
 */ export var PrefixedLink = function(_param) {
    var to = _param.to, props = _object_without_properties(_param, [
        "to"
    ]);
    var prefixedPath = typeof to === 'string' ? getRoutePath(to) : to;
    return /*#__PURE__*/ React.createElement(Link, _object_spread({
        to: prefixedPath
    }, props));
};
/**
 * 환경에 맞는 경로 접두사를 자동으로 적용하는 useNavigate 훅
 * @returns navigate 함수 (경로에 자동으로 접두사 적용)
 */ export var usePrefixedNavigate = function() {
    var navigate = useNavigate();
    return React.useCallback(function(path) {
        if (typeof path === 'number') {
            // 숫자인 경우 (뒤로가기/앞으로가기) 그대로 전달
            navigate(path);
        } else {
            // 문자열인 경우 경로 접두사 적용
            var prefixedPath = getRoutePath(path);
            navigate(prefixedPath);
        }
    }, [
        navigate
    ]);
};
